export const TOGGLEABLE_TERMINAL_PANEL_IDS = ['favorites', 'market', 'orderBook', 'accountStatus'] as const;
export type ToggleableTerminalPanelId = (typeof TOGGLEABLE_TERMINAL_PANEL_IDS)[number];
export type TerminalPanelVisibility = Record<ToggleableTerminalPanelId, boolean>;

export const DEFAULT_TERMINAL_PANELS: TerminalPanelVisibility = {
  favorites: true,
  market: true,
  orderBook: true,
  accountStatus: true,
};

export function mergeTerminalPanels(stored: Partial<TerminalPanelVisibility> | undefined | null): TerminalPanelVisibility {
  return { ...DEFAULT_TERMINAL_PANELS, ...stored };
}

export interface TerminalLayoutSizes {
  primaryRowHeight: number;
  orderBookWidth: number;
  rightColumnWidth: number;
}

export const DEFAULT_TERMINAL_LAYOUT_SIZES: TerminalLayoutSizes = {
  primaryRowHeight: 688,
  orderBookWidth: 246,
  rightColumnWidth: 320,
};

export const TERMINAL_LAYOUT_LIMITS = {
  /** Min/max height of the entire top trading row (chart + order book + order form). */
  primaryRowHeight: { min: 600, max: 900 },
  tablesRowHeight: { min: 240 },
  /** Min width of the flexible chart track; it absorbs remaining space via `1fr`. */
  chartWidth: { min: 200 },
  orderBookWidth: { min: 200, max: 600 },
  rightColumnWidth: { min: 269, max: 600 },
} as const;

export function clampTerminalLayoutSizes(sizes: Partial<TerminalLayoutSizes>): TerminalLayoutSizes {
  return {
    primaryRowHeight: clamp(
      sizes.primaryRowHeight ?? DEFAULT_TERMINAL_LAYOUT_SIZES.primaryRowHeight,
      TERMINAL_LAYOUT_LIMITS.primaryRowHeight.min,
      TERMINAL_LAYOUT_LIMITS.primaryRowHeight.max
    ),
    orderBookWidth: clamp(
      sizes.orderBookWidth ?? DEFAULT_TERMINAL_LAYOUT_SIZES.orderBookWidth,
      TERMINAL_LAYOUT_LIMITS.orderBookWidth.min,
      TERMINAL_LAYOUT_LIMITS.orderBookWidth.max
    ),
    rightColumnWidth: clamp(
      sizes.rightColumnWidth ?? DEFAULT_TERMINAL_LAYOUT_SIZES.rightColumnWidth,
      TERMINAL_LAYOUT_LIMITS.rightColumnWidth.min,
      TERMINAL_LAYOUT_LIMITS.rightColumnWidth.max
    ),
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export interface TerminalGridLayoutConfig {
  reverseChartPosition: boolean;
  handleGap: string;
  showOrderBook: boolean;
}

/**
 * The desktop terminal grid is described by a single ordered list of column tracks. Everything
 * else — the `grid-template-columns` string, each panel's grid line, and the tables span — is
 * derived from this order so the three can't drift out of sync.
 *
 * Normal orientation runs chart → order book → right column; `reverseChartPosition` mirrors it.
 * The right column and its resize handle are always the two tracks pinned to the far edge.
 */
export type TerminalColumnId = 'chart' | 'orderBookHandle' | 'orderBook' | 'rightColumnHandle' | 'rightColumn';

export function getTerminalColumnOrder(showOrderBook: boolean, reverseChartPosition: boolean): TerminalColumnId[] {
  const order: TerminalColumnId[] = showOrderBook
    ? ['chart', 'orderBookHandle', 'orderBook', 'rightColumnHandle', 'rightColumn']
    : ['chart', 'rightColumnHandle', 'rightColumn'];
  return reverseChartPosition ? [...order].reverse() : order;
}

export type TerminalColumnLines = Record<TerminalColumnId, number | undefined>;

/** 1-based grid line each column cell starts at, or `undefined` when the cell is absent (order book hidden). */
export function getTerminalColumnLines(showOrderBook: boolean, reverseChartPosition: boolean): TerminalColumnLines {
  const lines: TerminalColumnLines = {
    chart: undefined,
    orderBookHandle: undefined,
    orderBook: undefined,
    rightColumnHandle: undefined,
    rightColumn: undefined,
  };
  getTerminalColumnOrder(showOrderBook, reverseChartPosition).forEach((id, index) => {
    lines[id] = index + 1;
  });
  return lines;
}

function columnTrackSize(id: TerminalColumnId, sizes: TerminalLayoutSizes, handleGap: string): string {
  switch (id) {
    case 'chart':
      return `minmax(${TERMINAL_LAYOUT_LIMITS.chartWidth.min}px, 1fr)`;
    case 'orderBook':
      return `${sizes.orderBookWidth}px`;
    case 'rightColumn':
      return `minmax(${TERMINAL_LAYOUT_LIMITS.rightColumnWidth.min}px, ${sizes.rightColumnWidth}px)`;
    case 'orderBookHandle':
    case 'rightColumnHandle':
      return handleGap;
  }
}

export function buildGridTemplateColumns(sizes: TerminalLayoutSizes, config: TerminalGridLayoutConfig): string {
  return getTerminalColumnOrder(config.showOrderBook, config.reverseChartPosition)
    .map((id) => columnTrackSize(id, sizes, config.handleGap))
    .join(' ');
}

export function buildGridTemplateRows(sizes: TerminalLayoutSizes, handleGap: string): string {
  return `${sizes.primaryRowHeight}px ${handleGap} minmax(${TERMINAL_LAYOUT_LIMITS.tablesRowHeight.min}px, 1fr)`;
}

/**
 * The tables panel (bottom row) spans every column except the right column and its handle.
 * Reversed, those two sit at the start (lines 1–2), so tables run from line 3 to the end; normal,
 * they sit at the end, so tables run from line 1 up to the right-column handle.
 */
export function getTablesGridColumn(showOrderBook: boolean, showAccount: boolean, reverseChartPosition: boolean): string {
  if (!showAccount) {
    return '1 / -1';
  }
  if (reverseChartPosition) {
    return '3 / -1';
  }
  const trackCount = getTerminalColumnOrder(showOrderBook, reverseChartPosition).length;
  return `1 / ${trackCount - 1}`;
}

export function applyTerminalGridLayout(element: HTMLElement, sizes: TerminalLayoutSizes, config: TerminalGridLayoutConfig) {
  element.style.gridTemplateColumns = buildGridTemplateColumns(sizes, config);
  element.style.gridTemplateRows = buildGridTemplateRows(sizes, config.handleGap);
}
